import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * ExpandableText — long body copy clamped to a few lines with a trailing ellipsis.
 * When the copy overflows the clamp, the text itself becomes the toggle: click
 * (or Enter/Space) the truncated text to expand it, click again to collapse. No
 * separate "Show more" control — the ellipsis is the affordance. Short copy that
 * fits stays plain, non-interactive text. Used for the home action-center card's
 * rationale and anywhere a description shouldn't push the layout tall by default.
 *
 *   <ExpandableText lines={2}>{longRationale}</ExpandableText>
 *
 * The clamp is applied via inline style (not a Tailwind `line-clamp-*` class) so
 * `lines` is fully dynamic and no new utility class needs to be generated. Since
 * the whole text region becomes the toggle when clamped, use it for plain copy —
 * not for text that itself contains links or other click targets.
 */

export interface ExpandableTextProps
  extends Omit<React.HTMLAttributes<HTMLParagraphElement>, 'children'> {
  /** Lines shown while collapsed (default 2). */
  lines?: number
  /** Extra classes for the text (e.g. color / size tokens). */
  textClassName?: string
  children: React.ReactNode
}

export const ExpandableText = React.forwardRef<
  HTMLParagraphElement,
  ExpandableTextProps
>(({ lines = 2, className, textClassName, children, ...props }, ref) => {
  const textRef = React.useRef<HTMLParagraphElement>(null)
  const [expanded, setExpanded] = React.useState(false)
  const [overflowing, setOverflowing] = React.useState(false)

  // Merge the forwarded ref with the internal measurement ref.
  const setRefs = React.useCallback(
    (node: HTMLParagraphElement | null) => {
      textRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [ref]
  )

  // Measure overflow only while collapsed (the clamp is off when expanded, so
  // scrollHeight would equal clientHeight and falsely read as not overflowing).
  // `overflowing` stays sticky through an expand so the toggle affordance holds.
  React.useEffect(() => {
    if (expanded) return
    const el = textRef.current
    if (!el) return
    let cancelled = false
    const check = () => {
      if (!cancelled) setOverflowing(el.scrollHeight > el.clientHeight + 1)
    }
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    // Re-check once webfonts load: a font swap can push the text past the clamp
    // while the clamped clientHeight stays pinned at `lines * lineHeight`, so the
    // ResizeObserver won't fire — a pre-font measurement would miss the overflow
    // and the expand affordance would never appear.
    document.fonts?.ready.then(check)
    return () => {
      cancelled = true
      ro.disconnect()
    }
  }, [expanded, children, lines])

  const interactive = overflowing
  const toggle = () => setExpanded((v) => !v)

  return (
    <p
      ref={setRefs}
      className={cn(
        'text-fbui-text-body',
        interactive &&
          'cursor-pointer rounded outline-none transition-colors hover:text-fbui-gray-700 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand',
        className,
        textClassName
      )}
      style={
        expanded
          ? undefined
          : {
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: lines,
              overflow: 'hidden',
            }
      }
      // Spread consumer props first so the disclosure wiring below always wins
      // (role/tabIndex/aria-expanded can't be silently dropped), and compose the
      // handlers so a consumer onClick/onKeyDown still fires alongside the toggle.
      {...props}
      {...(interactive
        ? {
            // The visible text is the accessible name (this is a disclosure whose
            // trigger is its own content) + aria-expanded conveys the state. We
            // deliberately do NOT set aria-label here: it would override the
            // paragraph text in the accname, hiding the rationale from AT.
            role: 'button',
            tabIndex: 0,
            'aria-expanded': expanded,
            onClick: (e: React.MouseEvent<HTMLParagraphElement>) => {
              props.onClick?.(e)
              toggle()
            },
            onKeyDown: (e: React.KeyboardEvent<HTMLParagraphElement>) => {
              props.onKeyDown?.(e)
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                toggle()
              }
            },
          }
        : {})}
    >
      {children}
    </p>
  )
})
ExpandableText.displayName = 'ExpandableText'
